'use client';

import { useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/ui/loader";
import { ButtonLoader } from "@/components/ui/button-loader";
import Image from "next/image";

function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken();
      
      console.log('[Login] Token ottenuto:', { 
        tokenLength: token.length,
        userEmail: userCredential.user.email 
      });

      const cookieResponse = await fetch('/api/auth/set-cookie', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
        credentials: 'include'
      });

      if (!cookieResponse.ok) {
        throw new Error('Errore durante l\'impostazione del cookie');
      }

      toast.success('Login effettuato con successo');

      let attempts = 0;
      const maxAttempts = 5;
      const checkCookie = async () => {
        const hasCookie = document.cookie.includes('__session');
        console.log('[Login] Verifica cookie:', { 
          attempt: attempts + 1, 
          hasCookie,
          cookies: document.cookie 
        });

        if (hasCookie) {
          const from = searchParams?.get('from') || '/dashboard';
          router.push(from);
        } else if (attempts < maxAttempts) {
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 500));
          await checkCookie();
        } else {
          console.log('[Login] Cookie non rilevato dopo i tentativi, procedo comunque');
          const from = searchParams?.get('from') || '/dashboard';
          router.push(from);
        }
      };

      await checkCookie();
    } catch (error) {
      console.error('[Login] Errore:', error);
      toast.error('Errore durante il login. Riprova.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#911c21] p-4">
      <div className="w-full max-w-md space-y-8 bg-white p-6 rounded-lg shadow-xl">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <Image
              src="/logo-2.png"
              alt="Logo"
              width={120}
              height={120}
              priority
              className="object-contain"
            />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">
            Accedi al tuo account
          </h2>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleLogin}>
          <div className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full"
              />
            </div>
            <div>
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full flex items-center justify-center gap-2"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <ButtonLoader />
                <span>Caricamento...</span>
              </>
            ) : (
              'Accedi'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-[#911c21]">
        <Loader />
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
} 